import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
  Link, Paper, Snackbar, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import api from "../../api";
import { CARD_SX } from "./AnalyticsPageShell";
import {
  METRICS_URL, METRIC_ORDER, STATIC_NOTE, brandList, fallbackMetrics, formatDate, formatMetric, indexMetrics, refreshSummary,
} from "./competitorMetrics";

const PURPLE = "#7c3aed";

/**
 * Loads the Competitors tab figures. If the API fails, `rows` falls back to the static research
 * so the page never breaks (`isStatic` is then true and nothing is editable).
 */
export function useCompetitorMetrics() {
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    rows: fallbackMetrics(),
    isStatic: true,
    lastRefreshedAt: null,
    youtubeConfigured: true,
    loadError: "",
  });
  const [toast, setToast] = useState(null);

  const applyPayload = useCallback((data, extra = {}) => {
    setState((s) => ({
      ...s,
      ...extra,
      rows: Array.isArray(data?.metrics) && data.metrics.length ? data.metrics : s.rows,
      isStatic: !(Array.isArray(data?.metrics) && data.metrics.length) && s.isStatic,
      lastRefreshedAt: data?.last_refreshed_at ?? s.lastRefreshedAt,
      youtubeConfigured: data?.youtube_configured ?? s.youtubeConfigured,
      loadError: "",
    }));
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(METRICS_URL);
        if (alive) applyPayload(res.data?.data || res.data, { loading: false });
      } catch (e) {
        if (alive) {
          setState((s) => ({ ...s, loading: false, loadError: e?.response?.data?.message || STATIC_NOTE }));
        }
      }
    })();
    return () => { alive = false; };
  }, [applyPayload]);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, refreshing: true }));
    try {
      const res = await api.post(`${METRICS_URL}/refresh`);
      const body = res.data || {};
      applyPayload(body.data, { refreshing: false });
      setToast(refreshSummary(body.data?.refresh, body.message));
    } catch (e) {
      const body = e?.response?.data;
      if (body?.data) applyPayload(body.data);
      setState((s) => ({ ...s, refreshing: false }));
      setToast({ severity: e?.response?.status === 429 ? "info" : "error", text: body?.message || "Refresh failed. Try again in a few minutes." });
    }
  }, [applyPayload]);

  const saveMetric = useCallback(async (row, payload) => {
    const res = await api.patch(`${METRICS_URL}/${row.id}`, payload);
    const updated = res.data?.data;
    if (updated) {
      setState((s) => ({ ...s, rows: s.rows.map((r) => (r.id === updated.id ? updated : r)) }));
    }
    setToast({ severity: "success", text: `${row.competitor_name}: ${row.label || row.metric_key} saved.` });
    return updated;
  }, []);

  const index = useMemo(() => indexMetrics(state.rows), [state.rows]);

  return { ...state, index, refresh, saveMetric, toast, closeToast: () => setToast(null) };
}

/** "Last refreshed" + Refresh button + YouTube key notice + result snackbar. */
export function CompetitorRefreshBar({ metrics }) {
  const { loading, refreshing, isStatic, lastRefreshedAt, youtubeConfigured, loadError, refresh, toast, closeToast } = metrics;
  const when = lastRefreshedAt ? formatDate(lastRefreshedAt, true) : null;

  return (
    <Paper sx={{ ...CARD_SX, mb: 2 }} data-testid="competitors-refresh-bar">
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} justifyContent="space-between">
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }} data-testid="competitors-last-refreshed">
            {loading ? "Loading live figures…" : `Last refreshed: ${when || "not yet"}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Refresh pulls App Store ratings and YouTube statistics now. Other figures are entered by hand (pencil icon).
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={refresh}
          disabled={loading || refreshing}
          startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: "#6d28d9" }, whiteSpace: "nowrap" }}
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
      </Stack>
      {!loading && isStatic && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="competitors-static-notice">{loadError || STATIC_NOTE}</Alert>
      )}
      {!loading && !isStatic && !youtubeConfigured && (
        <Alert severity="info" sx={{ mt: 1.5 }} data-testid="competitors-youtube-notice">
          YouTube API key not configured, so YouTube figures can't refresh. Add YOUTUBE_API_KEY to the backend .env.
        </Alert>
      )}
      <Snackbar open={!!toast} autoHideDuration={8000} onClose={closeToast} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? (
          <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ width: "100%" }} data-testid="competitors-toast">
            {toast.text}
          </Alert>
        ) : <span />}
      </Snackbar>
    </Paper>
  );
}

function SourceChip({ row }) {
  if (row.source === "static") return <Chip size="small" label="Saved" variant="outlined" sx={{ height: 20, fontSize: 11 }} />;
  const live = row.source === "live";
  return (
    <Chip
      size="small"
      label={live ? "Live" : "Manual"}
      color={live ? "success" : "default"}
      variant={live ? "filled" : "outlined"}
      sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
    />
  );
}

function FigureLine({ row, editable, onEdit }) {
  const checked = formatDate(row.last_checked_at);
  const value = formatMetric(row);
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ py: 0.5, borderBottom: "1px dashed #eef0f4" }} data-testid={`figure-${row.competitor_key}-${row.metric_key}`}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>{row.label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.source_url ? (
            <Link href={row.source_url} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">{value}</Link>
          ) : value}
        </Typography>
        {row.last_error && (
          <Typography variant="caption" color="error" sx={{ display: "block" }}>{row.last_error}</Typography>
        )}
      </Box>
      <Stack alignItems="flex-end" spacing={0.25}>
        <SourceChip row={row} />
        {checked && <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>checked {checked}</Typography>}
      </Stack>
      {editable && (
        <Tooltip title="Edit figure">
          <IconButton size="small" onClick={() => onEdit(row)} aria-label={`Edit ${row.competitor_name} ${row.label}`}>
            <EditIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

export function EditMetricDialog({ row, onClose, onSave }) {
  const numeric = row && row.kind !== "text" && row.kind !== "date";
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!row) return;
    setValue(row.value === null || row.value === undefined ? "" : String(row.value));
    setNote(row.value_text || "");
    setUrl(row.source_url || "");
    setError("");
  }, [row]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { value_text: note.trim() || null, source_url: url.trim() || null };
    if (numeric) payload.value = value.trim() === "" ? null : Number(value);
    if (numeric && payload.value !== null && (Number.isNaN(payload.value) || payload.value < 0)) {
      setError("Enter a number of 0 or more.");
      return;
    }
    if ((payload.value === null || payload.value === undefined) && !payload.value_text) {
      setError(numeric ? "Enter a number or a short note." : "Enter a value.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave(row, payload);
      onClose();
    } catch (err) {
      const errors = err?.response?.data?.errors;
      setError((errors && Object.values(errors).flat()[0]) || err?.response?.data?.message || "Could not save the figure.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!row} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      {row && (
        <form onSubmit={submit} noValidate>
          <DialogTitle sx={{ pb: 0.5 }}>{row.competitor_name}: {row.label}</DialogTitle>
          <DialogContent>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Saved as a manual figure, checked today.
            </Typography>
            <Stack spacing={1.5}>
              {numeric && (
                <TextField
                  label={row.kind === "rating" ? "Rating (0-5)" : "Number"}
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  inputProps={{ min: 0, step: "any" }}
                  size="small"
                  autoFocus
                />
              )}
              <TextField
                label={numeric ? "Note (optional, e.g. Not found)" : "Value"}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                inputProps={{ maxLength: 120 }}
                size="small"
                autoFocus={!numeric}
              />
              <TextField label="Source link (optional)" value={url} onChange={(e) => setUrl(e.target.value)} size="small" />
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: "#6d28d9" } }}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}

/** One card per brand with each figure, its Live/Manual chip and checked date. */
export function CompetitorFiguresGrid({ metrics }) {
  const { index, isStatic, saveMetric } = metrics;
  const [editing, setEditing] = useState(null);
  const brands = brandList(index);

  return (
    <>
      <Grid container spacing={1.5}>
        {brands.map((b) => {
          const rows = METRIC_ORDER.map((k) => b.metrics[k]).filter(Boolean);
          return (
            <Grid item xs={12} sm={6} lg={4} key={b.key}>
              <Box sx={{ p: 1.5, border: `1px solid ${b.isOurs ? "#e9d5ff" : "#eef0f4"}`, bgcolor: b.isOurs ? "#faf5ff" : "transparent", borderRadius: 2, height: "100%" }} data-testid={`competitors-figure-card-${b.key}`}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 700 }}>{b.name}</Typography>
                  {b.isOurs && <Chip size="small" label="Us" sx={{ bgcolor: PURPLE, color: "#fff", height: 20, fontSize: 11 }} />}
                </Stack>
                {rows.map((r) => (
                  <FigureLine key={r.id} row={r} editable={!isStatic && r.source === "manual"} onEdit={setEditing} />
                ))}
              </Box>
            </Grid>
          );
        })}
      </Grid>
      <EditMetricDialog row={editing} onClose={() => setEditing(null)} onSave={saveMetric} />
    </>
  );
}
